import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

// Formater une date
export const formatDate = (dateString, formatStr = 'dd/MM/yyyy HH:mm') => {
  if (!dateString) return '-';
  try {
    return format(parseISO(dateString), formatStr, { locale: fr });
  } catch {
    return dateString;
  }
};

// Formater une date relative
export const formatRelative = (dateString) => {
  if (!dateString) return '-';
  try {
    return formatDistanceToNow(parseISO(dateString), { 
      addSuffix: true, 
      locale: fr 
    });
  } catch {
    return dateString;
  }
};

// Labels des statuts
export const statusLabels = {
  open: 'Ouvert',
  in_progress: 'En cours',
  waiting_user: 'En attente utilisateur',
  waiting_third_party: 'En attente tiers',
  resolved: 'Résolu',
  closed: 'Fermé'
};

export const getStatusLabel = (status) => statusLabels[status] || status;

// Labels des priorités
export const priorityLabels = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  urgent: 'Urgente'
};

export const getPriorityLabel = (priority) => priorityLabels[priority] || priority;

// Labels des rôles
export const roleLabels = {
  user: 'Utilisateur',
  agent: 'Agent',
  admin: 'Administrateur',
  superadmin: 'Super Admin'
};

export const getRoleLabel = (role) => roleLabels[role] || role;

// Couleurs des statuts pour les graphiques
export const statusColors = {
  open: '#EF4444',
  in_progress: '#3B82F6',
  waiting_user: '#F59E0B',
  waiting_third_party: '#8B5CF6',
  resolved: '#10B981',
  closed: '#6B7280'
};

// Couleurs des priorités
export const priorityColors = {
  low: '#6B7280',
  medium: '#3B82F6',
  high: '#F97316',
  urgent: '#EF4444'
};

// Tronquer un texte
export const truncate = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Générer des initiales
export const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Debounce
export const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

// Classer les tickets par priorité
export const priorityOrder = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3
};

export const sortByPriority = (tickets) => {
  return [...tickets].sort((a, b) => {
    return (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
  });
};

// Vérifier si un ticket est actif
export const isActiveTicket = (ticket) => {
  return !['resolved', 'closed'].includes(ticket.status);
};

// Format File Size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Couleurs Tailwind des statuts (pour les badges/pills)
export const statusBadgeClasses = {
  open: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  waiting_user: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  waiting_third_party: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
};

export const getStatusBadgeClass = (status) => statusBadgeClasses[status] || statusBadgeClasses.open;

// Couleurs Tailwind des priorités (pour les badges/pills)
export const priorityBadgeClasses = {
  low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400',
  medium: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  urgent: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
};

export const getPriorityBadgeClass = (priority) => priorityBadgeClasses[priority] || priorityBadgeClasses.medium;

// SLA helpers centralisés
export const getSlaStatus = (ticket) => {
  if (!ticket.sla_due_at) return null;
  const now = new Date();
  const due = new Date(ticket.sla_due_at);
  const isActive = !['resolved', 'closed'].includes(ticket.status);

  if (!isActive) return 'ok';
  if (due < now) return 'breached';

  const hoursLeft = (due - now) / (1000 * 60 * 60);
  if (hoursLeft < 1) return 'warning';
  return 'ok';
};

export const slaStatusColors = {
  ok: 'text-green-600',
  warning: 'text-amber-600',
  breached: 'text-red-600'
};
